import React from 'react';
import PropTypes from 'prop-types';
import styles from './TruncatedText.module.scss';

/* ────────────────────────────────────────────────────────────
 * TruncatedText — single-line text that ellipsizes when it
 * overflows its container and reveals the full string in a
 * native tooltip, but ONLY when it is actually clipped.
 *
 *   <TruncatedText text={item.description} />
 *   <TruncatedText as="span" className="foo" text={name} />
 *   <TruncatedText text={item.test_steps} maxChars={30} />
 *
 * Two truncation mechanisms, usable together:
 *   - CSS clip (always on) — the value fills the cell and
 *     ellipsizes at whatever width the column happens to be.
 *   - `maxChars` (opt-in) — a hard character cap so a 2000-char
 *     free-text field can never stretch a column, regardless of
 *     available width. The visible text becomes `…`-suffixed.
 *
 * Overflow is measured lazily on hover / focus (scrollWidth >
 * clientWidth) and the `title` attribute is set just-in-time —
 * so there are no per-cell ResizeObservers and short values
 * never get a redundant tooltip. A `maxChars` trim always sets
 * the tooltip, since the clip is in the text itself and the
 * browser has nothing to measure. `title` carries the full,
 * untrimmed string in both cases, and `aria-label` keeps it
 * available to assistive tech when the DOM text is shortened.
 * The tooltip mechanism matches the design system's
 * native-`title` convention (see IconButton).
 * ──────────────────────────────────────────────────────────── */

const ELLIPSIS = '…';

const TruncatedText = ({ text, as: Tag, className, maxChars, ...rest }) => {
  const value = text == null ? '' : String(text);

  const trimmed = maxChars > 0 && value.length > maxChars;
  const display = trimmed ? `${value.slice(0, maxChars).trimEnd()}${ELLIPSIS}` : value;

  // Reveal the tooltip when the text is genuinely hidden — either
  // cut by `maxChars` or clipped by the column width. Set
  // imperatively on enter/focus — the browser reads `title` after
  // its own hover delay, which is well after this fires.
  const reveal = (e) => {
    const el = e.currentTarget;
    const clipped = el.scrollWidth - el.clientWidth > 1; // sub-pixel slack
    el.title = trimmed || clipped ? value : '';
  };

  return (
    <Tag
      className={[styles.truncate, className].filter(Boolean).join(' ')}
      onMouseEnter={reveal}
      onFocus={reveal}
      aria-label={trimmed ? value : undefined}
      {...rest}
    >
      {display}
    </Tag>
  );
};

TruncatedText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Element/tag to render — defaults to a <span>.
  as: PropTypes.elementType,
  className: PropTypes.string,
  // Hard character cap. 0 / undefined = CSS clipping only.
  maxChars: PropTypes.number,
};

TruncatedText.defaultProps = {
  text: '',
  as: 'span',
  className: '',
  maxChars: 0,
};

export default TruncatedText;
